import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import {
  BanknotesIcon,
  BriefcaseIcon,
  ChartBarSquareIcon,
  ClipboardDocumentListIcon,
  ClockIcon,
  DocumentTextIcon,
  GlobeEuropeAfricaIcon,
  IdentificationIcon,
  PhotoIcon,
  SparklesIcon,
  WrenchScrewdriverIcon,
} from "@heroicons/react/24/outline";

import LoadingWheel from "../../components/shared/LoadingWheel";
import { usePageNameContext } from "../../contexts/PageTitleContext";

import PartVehicleDetails from "../../components/leads/LeadParts/PartVehicleDetails";
import useVehicleSingle from "../../data/useVehicleSingle";
import VehiclePartsCard from "../../components/vehicles/VehiclePartsCard";
import VehicleNotes from "../../components/leads/LeadInformation/VehicleNotes";
import AwaitingInventoryStatusDropdown from "../../components/awaitingInventory/AwaitingInventoryStatusDropdown";
import VehicleLocationDropdown from "../../components/vehicles/VehicleLocationDropdown";
import YardStatusDropdown from "../../components/awaitingInventory/YardStatusDropdown";
import PartsPriorityDropdown from "../../components/awaitingInventory/PartsPriorityDropdown";
import VehicleJobs from "../../components/vehicles/VehicleJobs";
import CreateJobs from "../../components/jobs/CreateJobs";
import LeadNotes from "../../components/leads/LeadInformation/LeadNotes";
import LeadDocuments from "../../components/leads/LeadDocuments";
import PartVehicleImages from "../../components/leads/LeadParts/LeadImagesCarousel";
import VehicleWorkflowCard from "../../components/vehicles/VehicleWorkflowCard";
import VehicleHeader from "../../components/vehicles/VehicleHeader";
import VehicleTimeline from "../../components/vehicles/VehicleTimeline";
import VehicleFinancialStats from "../../components/vehicles/VehicleFinancialStats";
import VehicleIntelligenceCard from "../../components/vehicles/VehicleIntelligenceCard";
import VehicleSustainability from "../../components/vehicles/VehicleSustainability";
import LinkedWorkOrdersCard from "../../components/workOrders/LinkedWorkOrdersCard";
import ConfirmModal from "../../components/shared/Confirm/ConfirmModal";
import Card from "../../components/shared/Card/Card";
import { ButtonInput } from "../../components/shared/Inputs/ButtonInput";
import useVehiclesMutations from "../../data/useVehiclesMutations";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import classNames from "../../utils/classNames";
import openInNewTab from "../../utils/openInNewTab";

// The vehicle page shares its visual grammar with the individual parts
// stock page (`ViewStock`) so operators jumping between the two screens see
// the same shell: a persistent header/details/workflow block up top and an
// inline tab nav below it for deep-dive content.
type TabId =
  | "info"
  | "timeline"
  | "parts"
  | "financial"
  | "intelligence"
  | "sustainability"
  | "images"
  | "jobs"
  | "notes"
  | "workOrders"
  | "documents";

interface TabDef {
  id: TabId;
  label: string;
  disabled?: boolean;
  hidden?: boolean;
  icon?: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  /** Gradient/sparkly accent — mirrors the "fancy" treatment in ViewStock. */
  fancy?: boolean;
}

export default function Vehicle() {
  const { setInfo } = usePageNameContext();
  const navigate = useNavigate();
  const { vehicleId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { vehicle, lead } = useVehicleSingle(vehicleId);
  const { softDelete } = useVehiclesMutations();
  const { deleteHandlers } = useDefaultCRUDHandlers("Vehicle");
  const createJobsTargetId = `vehicle-create-jobs-${vehicleId}`;

  // Deep-link support — `?tab=jobs&scrollTo=createJobs` is used by the
  // Awaiting Inventory row's "Create dismantling list" button so operators
  // land on the right section on arrival.
  const initialTab = (searchParams.get("tab") as TabId | null) ?? "info";
  const [activeTab, setActiveTab] = useState<TabId>(initialTab);
  const [deleteOpen, setDeleteOpen] = useState(false);

  useEffect(() => {
    if (!vehicle.data?.data && !vehicle.isLoading) {
      setInfo({
        name: "Vehicle not found",
        desc: "The vehicle you are looking for does not exist",
      });
      return;
    }

    if (!vehicle.data?.data && vehicle.isLoading) {
      setInfo({
        name: "",
        desc: "",
      });
      return;
    }

    setInfo({
      name: `${vehicle.data?.data?.vrm} ${vehicle.data?.data?.combinedMake} ${vehicle.data?.data?.modelRangeDescription}`,
      desc: `View and manage ${vehicle.data?.data?.vrm}`,
    });
  }, [vehicleId, vehicle.isLoading]);

  const vehicleData = vehicle.data?.data;
  const leadData = lead.data?.data;

  // Keep the active tab in sync with the URL so navigations that pass a
  // `?tab=` also switch the view when the component is already mounted.
  useEffect(() => {
    const urlTab = searchParams.get("tab") as TabId | null;
    if (urlTab && urlTab !== activeTab) {
      setActiveTab(urlTab);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Honour `?scrollTo=createJobs` once the vehicle data (and therefore the
  // scroll target) has actually rendered. Cleared after firing so switching
  // tabs manually doesn't keep re-scrolling.
  useEffect(() => {
    if (!vehicleData) return;
    const scrollTarget = searchParams.get("scrollTo");
    if (scrollTarget !== "createJobs") return;
    if (activeTab !== "jobs") return;

    const timeout = window.setTimeout(() => {
      const target = document.getElementById(createJobsTargetId);
      if (target) {
        target.scrollIntoView({ behavior: "smooth", block: "start" });
      }
      const next = new URLSearchParams(searchParams);
      next.delete("scrollTo");
      setSearchParams(next, { replace: true });
    }, 150);

    return () => window.clearTimeout(timeout);
  }, [vehicleData, activeTab, searchParams, createJobsTargetId, setSearchParams]);

  const tabs = useMemo<TabDef[]>(() => {
    if (!vehicleData) return [];
    const hasLead = !!leadData;
    return [
      {
        id: "info",
        label: "Vehicle Information",
        icon: IdentificationIcon,
      },
      {
        id: "timeline",
        label: "Timeline",
        icon: ClockIcon,
      },
      {
        id: "parts",
        label: "Parts",
        icon: WrenchScrewdriverIcon,
      },
      {
        id: "financial",
        label: "Financial Stats",
        icon: BanknotesIcon,
      },
      {
        id: "intelligence",
        label: "Vehicle Intelligence",
        icon: SparklesIcon,
        fancy: true,
      },
      {
        id: "sustainability",
        label: "Sustainability",
        icon: GlobeEuropeAfricaIcon,
      },
      {
        id: "images",
        label: "Images",
        icon: PhotoIcon,
        disabled: !hasLead,
      },
      {
        id: "jobs",
        label: "Jobs",
        icon: BriefcaseIcon,
      },
      {
        id: "notes",
        label: "Notes",
        icon: ClipboardDocumentListIcon,
      },
      {
        id: "workOrders",
        label: "Work Orders",
        icon: ChartBarSquareIcon,
      },
      {
        id: "documents",
        label: "Documents",
        icon: DocumentTextIcon,
        disabled: !hasLead,
      },
    ];
  }, [vehicleData, leadData]);

  if (vehicle.isLoading || !vehicleData) {
    return (
      <div className="mt-8 min-h-[50vh] my-4 p-4 bg-white rounded-lg shadow">
        <LoadingWheel />
      </div>
    );
  }

  const handleDeleteVehicle = () => {
    if (!vehicleId) return;

    softDelete.mutate(vehicleId, {
      onSuccess: () => {
        deleteHandlers.onSuccess();
        setDeleteOpen(false);
        navigate("/vehicles");
      },
      onError: () => {
        deleteHandlers.onError();
      },
    });
  };

  const visibleTabs = tabs.filter((t) => !t.hidden);

  return (
    <>
      <div className="mt-4 flex flex-col gap-6">
        {/* Stock-style hero header — image, identifiers, lifecycle badges,
            price paid and total sales. Mirrors `StockHeader` so the vehicle
            and parts pages feel like the same product. */}
        <VehicleHeader
          vehicle={vehicleData}
          lead={leadData ?? null}
          locationControls={
            <>
              <div className="flex min-w-[12rem] flex-col rounded-lg border border-slate-200 bg-slate-50/60 px-3 py-2.5 shadow-sm">
                <p className="text-xs font-medium text-gray-500">Parts Status</p>
                <AwaitingInventoryStatusDropdown
                  vehicle={vehicleData}
                  classes="mt-1 w-full rounded-md border-slate-300 bg-white py-1.5 pl-3 pr-8 text-sm shadow-sm focus:border-primary-500 focus:ring-primary-500"
                />
              </div>
              <div className="flex min-w-[12rem] flex-col rounded-lg border border-slate-200 bg-slate-50/60 px-3 py-2.5 shadow-sm">
                <p className="text-xs font-medium text-gray-500">
                  Parts Priority Status
                </p>
                <PartsPriorityDropdown
                  vehicle={vehicleData}
                  classes="mt-1 w-full rounded-md border-slate-300 bg-white py-1.5 pl-3 pr-8 text-sm shadow-sm focus:border-primary-500 focus:ring-primary-500"
                />
              </div>
              <div className="flex min-w-[12rem] flex-col rounded-lg border border-slate-200 bg-slate-50/60 px-3 py-2.5 shadow-sm">
                <p className="text-xs font-medium text-gray-500">Yard Location</p>
                <VehicleLocationDropdown
                  vehicle={vehicleData}
                  classes="mt-1 w-full rounded-md border-slate-300 bg-white py-1.5 pl-3 pr-8 text-sm shadow-sm focus:border-primary-500 focus:ring-primary-500"
                />
              </div>
              <div className="flex min-w-[12rem] flex-col rounded-lg border border-slate-200 bg-slate-50/60 px-3 py-2.5 shadow-sm">
                <p className="text-xs font-medium text-gray-500">Yard Status</p>
                <YardStatusDropdown
                  vehicle={vehicleData}
                  classes="mt-1 w-full rounded-md border-slate-300 bg-white py-1.5 pl-3 pr-8 text-sm shadow-sm focus:border-primary-500 focus:ring-primary-500"
                />
              </div>
            </>
          }
          headerActions={
            <>
              {leadData?.id && (
                <ButtonInput
                  label="View Lead"
                  isSubmit={false}
                  onClick={() => openInNewTab(`/leads/${leadData.id}`)}
                  classes={undefined}
                />
              )}
              <ButtonInput
                label="Delete Vehicle"
                isSubmit={false}
                onClick={() => setDeleteOpen(true)}
                classes="!bg-red-600 hover:!bg-red-700"
              />
            </>
          }
        />

        {/* Always-visible workflow card — user explicitly asked to keep
            this above the tab switcher. The vehicle details block that used
            to sit alongside it is now the default tab body (see below). */}
        <VehicleWorkflowCard
          vehicle={vehicleData}
          createJobsTargetId={createJobsTargetId}
        />

        {/* Tab nav — mirrors the inline styling used on the ViewStock page
            (border-bottom, underline active state, optional fancy gradient
            treatment for intelligence-style tabs). */}
        <div className="flex flex-col gap-4">
          <div className="border-b border-gray-200">
            <nav
              className="-mb-px flex gap-4 sm:gap-8 overflow-x-auto"
              aria-label="Tabs"
            >
              {visibleTabs.map((tab) => {
                const isActive = tab.id === activeTab;
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    type="button"
                    disabled={tab.disabled}
                    onClick={() => setActiveTab(tab.id)}
                    className={classNames(
                      "group relative inline-flex items-center gap-1.5 whitespace-nowrap border-b-2 px-1 py-3 text-sm font-medium transition-colors",
                      tab.disabled
                        ? "border-transparent text-gray-300 cursor-not-allowed"
                        : tab.fancy && isActive
                          ? "border-violet-500 text-violet-700"
                          : tab.fancy
                            ? "border-transparent text-violet-600 hover:text-violet-700 hover:border-violet-300"
                            : isActive
                              ? "border-primary-600 text-primary-700"
                              : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                    )}
                  >
                    {Icon && (
                      <Icon
                        className={classNames(
                          "h-4 w-4 transition-transform",
                          tab.fancy && !tab.disabled
                            ? "text-violet-500 group-hover:scale-110"
                            : "",
                          tab.fancy && !tab.disabled && isActive
                            ? "drop-shadow-[0_0_6px_rgba(168,85,247,0.55)]"
                            : ""
                        )}
                        aria-hidden="true"
                      />
                    )}
                    {tab.fancy && !tab.disabled ? (
                      <span className="bg-gradient-to-r from-violet-600 via-fuchsia-500 to-sky-500 bg-clip-text text-transparent">
                        {tab.label}
                      </span>
                    ) : (
                      tab.label
                    )}
                  </button>
                );
              })}
            </nav>
          </div>

          <div>
            {activeTab === "info" && (
              <PartVehicleDetails
                vehicle={vehicleData}
                price={null}
                collectionQuestions
              />
            )}
            {activeTab === "timeline" && (
              <VehicleTimeline vehicle={vehicleData} lead={leadData ?? null} />
            )}
            {activeTab === "parts" && <VehiclePartsCard vehicle={vehicleData} />}
            {activeTab === "financial" && (
              <VehicleFinancialStats vehicle={vehicleData} />
            )}
            {activeTab === "intelligence" && (
              <VehicleIntelligenceCard vehicle={vehicleData} />
            )}
            {activeTab === "sustainability" && (
              <VehicleSustainability vehicle={vehicleData} />
            )}
            {activeTab === "images" &&
              (leadData ? (
                <PartVehicleImages lead={leadData} />
              ) : (
                <Card bodyClassName="p-6 text-sm text-gray-500">
                  No linked lead for images
                </Card>
              ))}
            {activeTab === "jobs" && (
              <div className="flex flex-col gap-6">
                <VehicleJobs vehicle={vehicleData} />
                <div id={createJobsTargetId}>
                  <CreateJobs lead={null} vehicle={vehicleData} />
                </div>
              </div>
            )}
            {activeTab === "notes" && (
              <div className="lg:grid lg:grid-cols-2 gap-4">
                <VehicleNotes vehicle={vehicleData} />
                {!lead.isLoading && leadData && (
                  <LeadNotes
                    lead={leadData}
                    notesProp="yardNotes"
                    title="Yard Notes"
                  />
                )}
              </div>
            )}
            {activeTab === "workOrders" && (
              <LinkedWorkOrdersCard
                linkedItems={[
                  {
                    id: vehicleData.id!,
                    name: `${vehicleData.vrm ?? "Unknown VRM"}${
                      vehicleData.combinedMake
                        ? ` - ${vehicleData.combinedMake}`
                        : ""
                    }${
                      vehicleData.combinedModel
                        ? ` ${vehicleData.combinedModel}`
                        : ""
                    }`,
                    type: "Vehicle",
                  },
                ]}
              />
            )}
            {activeTab === "documents" &&
              (leadData ? (
                <LeadDocuments
                  lead={leadData}
                  droppedFiles={undefined}
                  setDroppedFiles={undefined}
                />
              ) : (
                <Card bodyClassName="p-6 text-sm text-gray-500">
                  No linked lead for documents
                </Card>
              ))}
          </div>
        </div>
      </div>

      <ConfirmModal
        open={deleteOpen}
        setOpen={setDeleteOpen}
        onConfirm={handleDeleteVehicle}
        isLoading={softDelete.isLoading}
        title="Delete this vehicle?"
        message="This will soft-delete the vehicle. It will not appear in normal vehicle results unless deleted filters are explicitly included."
        confirmButtonText="Delete Vehicle"
      />
    </>
  );
}
